import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

export const INTAKE_STATES = [
  "not_started",
  "awaiting_verification",
  "pending_review",
  "completed",
] as const;

export type IntakeState = (typeof INTAKE_STATES)[number];

export interface IntakeStatus {
  required: boolean;
  state: IntakeState;
  ready: boolean;
}

export interface IntakeStart {
  url: string;
  expiresAt: string;
}

export const getIntakeStatusQueryKey = (userId: string | null | undefined) =>
  ["intake-status", userId ?? "signed-out"] as const;

export class IntakeClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IntakeClientError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function parseIntakeStatus(value: unknown): IntakeStatus {
  if (
    !isRecord(value) ||
    typeof value.required !== "boolean" ||
    typeof value.ready !== "boolean" ||
    typeof value.state !== "string" ||
    !INTAKE_STATES.includes(value.state as IntakeState)
  ) {
    throw new IntakeClientError("The intake status response was invalid.");
  }

  return {
    required: value.required,
    ready: value.ready,
    state: value.state as IntakeState,
  };
}

export async function fetchIntakeStatus(): Promise<IntakeStatus> {
  return parseIntakeStatus(await apiFetch("/intake/status"));
}

export function isAllowedIntakeUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 4096) return false;

  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      parsed.hostname === "form.jotform.com" &&
      parsed.pathname === "/262118899983073"
    );
  } catch {
    return false;
  }
}

export function parseIntakeStart(value: unknown): IntakeStart {
  if (
    !isRecord(value) ||
    !isAllowedIntakeUrl(value.url) ||
    typeof value.expiresAt !== "string"
  ) {
    throw new IntakeClientError("The intake launch response was invalid.");
  }

  const expiresAt = new Date(value.expiresAt);
  if (!Number.isFinite(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
    throw new IntakeClientError("The intake launch link has expired.");
  }

  return { url: value.url, expiresAt: value.expiresAt };
}

export async function startIntake(): Promise<IntakeStart> {
  // The server owns the account binding and opaque form token. The browser
  // sends no identity, return URL, or token and never stores the response.
  return parseIntakeStart(await apiFetch("/intake/start", { method: "POST" }));
}

/**
 * Keep return targets local to Plate OS. A hosted-form callback or a copied
 * query string must never turn this gate into an open redirect.
 */
export function normalizeIntakeReturnTo(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return "/dashboard";
  }

  try {
    const parsed = new URL(value, "https://plate-os.invalid");
    if (parsed.origin !== "https://plate-os.invalid") return "/dashboard";
    const normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    if (normalized === "/intake" || normalized.startsWith("/intake?")) {
      return "/dashboard";
    }
    return normalized || "/dashboard";
  } catch {
    return "/dashboard";
  }
}

/**
 * Use rel=noreferrer/noopener for the hosted launch. This keeps the
 * server-issued link out of analytics and prevents Plate OS URLs from being
 * sent as a referrer to the form.
 */
export function openHostedIntake(
  url: string,
  target: "_blank" | "_self" = "_blank",
  ownerDocument: Document = document,
): boolean {
  if (!isAllowedIntakeUrl(url)) return false;

  const anchor = ownerDocument.createElement("a");
  anchor.href = url;
  anchor.target = target;
  anchor.rel = "noreferrer noopener";
  anchor.tabIndex = -1;
  anchor.style.display = "none";
  ownerDocument.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
}

export function useIntakeStatus(
  userId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: getIntakeStatusQueryKey(userId),
    queryFn: fetchIntakeStatus,
    enabled: Boolean(userId) && enabled,
    // Intake is an account access decision. Never serve an offline or stale
    // result while the member route is being evaluated.
    networkMode: "online",
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  });
}